import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Upload, AlertCircle, CheckCircle, Video as VideoIcon, Link as LinkIcon } from 'lucide-react';
import { CreateVideoRequest, HOLIDAY_OPTIONS } from '@/types/video';
import { useAuth } from '@/contexts/AuthContext';
import { validateVideoUrl, getBasicVideoMetadata } from '@/utils/videoUtils';
import { toast } from 'sonner';

const VideoUpload: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [urlValidation, setUrlValidation] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  
  const [formData, setFormData] = useState<CreateVideoRequest>({
    video_url: '',
    title: '',
    description: '',
    song_name: '',
    artist_name: '',
    album_name: '',
    spotify_track_id: '',
    sequence_id: '',
    display_id: '',
    display_year: undefined,
    holiday: ''
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleUrlChange = (url: string) => {
    setFormData(prev => ({ ...prev, video_url: url }));
    
    if (!url.trim()) {
      setUrlValidation(null);
      setPreviewData(null);
      return;
    }

    const validation = validateVideoUrl(url);
    setUrlValidation(validation);

    if (validation.isValid) {
      const metadata = getBasicVideoMetadata(url);
      setPreviewData(metadata);
      
      // Auto-fill title if empty
      if (!formData.title && metadata) {
        setFormData(prev => ({
          ...prev,
          title: `${metadata.platform.charAt(0).toUpperCase() + metadata.platform.slice(1)} Light Display`
        }));
      }
    } else {
      setPreviewData(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!urlValidation?.isValid) {
      toast.error('Please enter a valid video URL');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Please enter a video title');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('supabase.auth.token');
      const response = await fetch('/api/videos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          display_year: formData.display_year ? parseInt(formData.display_year.toString()) : undefined
        })
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Video uploaded successfully!');
        navigate(`/videos/${result.id}`);
      } else {
        toast.error(result.error || 'Failed to upload video');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
    } finally {
      setLoading(false);
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push(year);
    }
    return years;
  };

  if (!user) {
    return null; // Will redirect
  }

  return (
    <>
      <Helmet>
        <title>Upload Video | LightsHunt</title>
        <meta name="description" content="Share your holiday light display video with the community" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        
        <main className="flex-1 container mx-auto px-4 py-6 pt-24">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2">Upload Video</h1>
              <p className="text-muted-foreground">
                Share your holiday light display video with the community
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Video URL */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="h-5 w-5" />
                    Video URL
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="video_url">YouTube or Vimeo URL *</Label>
                    <Input
                      id="video_url"
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={formData.video_url}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      required
                    />
                    
                    {urlValidation && (
                      <Alert className={`mt-2 ${urlValidation.isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-center gap-2">
                          {urlValidation.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                          <AlertDescription className={urlValidation.isValid ? 'text-green-700' : 'text-red-700'}>
                            {urlValidation.isValid ? 'Valid video URL detected' : urlValidation.error}
                          </AlertDescription>
                        </div>
                      </Alert>
                    )}

                    {previewData && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-12 bg-black rounded overflow-hidden shrink-0">
                            <img
                              src={previewData.thumbnailUrl}
                              alt="Video thumbnail"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </div>
                          <div>
                            <Badge variant="secondary" className="capitalize mb-1">
                              {previewData.platform}
                            </Badge>
                            <p className="text-sm text-muted-foreground">Video detected successfully</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Video Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <VideoIcon className="h-5 w-5" />
                    Video Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="My Amazing Christmas Light Display"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe your light display, setup details, or any interesting story behind it..."
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="holiday">Holiday</Label>
                      <Select value={formData.holiday} onValueChange={(value) => setFormData(prev => ({ ...prev, holiday: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select holiday" />
                        </SelectTrigger>
                        <SelectContent>
                          {HOLIDAY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="display_year">Display Year</Label>
                      <Select 
                        value={formData.display_year?.toString() || ''} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, display_year: value ? parseInt(value) : undefined }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent>
                          {generateYearOptions().map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Song Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Song Information</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Optional - helps others discover sequences by music
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="song_name">Song Name</Label>
                    <Input
                      id="song_name"
                      placeholder="Carol of the Bells"
                      value={formData.song_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, song_name: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="artist_name">Artist</Label>
                      <Input
                        id="artist_name"
                        placeholder="Trans-Siberian Orchestra"
                        value={formData.artist_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, artist_name: e.target.value }))}
                      />
                    </div>

                    <div>
                      <Label htmlFor="album_name">Album</Label>
                      <Input
                        id="album_name"
                        placeholder="Christmas Eve and Other Stories"
                        value={formData.album_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, album_name: e.target.value }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Options */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Options</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Link your video to existing sequences or displays
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      These fields can be left empty and updated later. Linking to sequences allows visitors to purchase the files used in your display.
                    </AlertDescription>
                  </Alert>
                  
                  <div>
                    <Label htmlFor="sequence_id">Sequence ID</Label>
                    <Input
                      id="sequence_id"
                      placeholder="Link to an existing sequence (optional)"
                      value={formData.sequence_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, sequence_id: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="display_id">Display ID</Label>
                    <Input
                      id="display_id"
                      placeholder="Link to an existing display (optional)"
                      value={formData.display_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_id: e.target.value }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Separator />

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/videos')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !urlValidation?.isValid}
                  className="flex-1"
                >
                  {loading ? 'Uploading...' : 'Upload Video'}
                </Button>
              </div>
            </form>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default VideoUpload;